import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Accordion,
  AccordionItem,
  EmptyStation,
  MessageBar,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { config } from '../../../../config';
import {
  ServiceDeploymentLogsDocument,
  ServiceDeploymentLogsOrderBy,
  type ServiceDeploymentLogsQuery,
} from '../../../../generated/hosting-service';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './ServiceDeploymentLogs.module.scss';

export const ServiceDeploymentLogs: React.FC = () => {
  const { envId, customizableServiceId, serviceDeploymentId } = useParams<{
    envId: string;
    customizableServiceId: string;
    serviceDeploymentId: string | undefined;
  }>();

  const [queryResult, setQueryResult] = useState<ServiceDeploymentLogsQuery>();
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    (async () => {
      // Check the status of hosting-service
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.HOSTING_SERVICE,
        envId,
      );

      if (statusMessage !== null) {
        setServiceStatusMessage(statusMessage);
      } else {
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
        );

        const { data } =
          await envApolloClient.query<ServiceDeploymentLogsQuery>({
            query: ServiceDeploymentLogsDocument,
            variables: {
              condition: serviceDeploymentId
                ? {
                    serviceDeploymentId,
                  }
                : {
                    isActiveDeployment: true,
                    serviceDefinitionId: customizableServiceId,
                  },
              orderBy: [ServiceDeploymentLogsOrderBy.TimestampDesc],
            },
            fetchPolicy: 'no-cache',
          });

        setQueryResult(data);
      }
    })();
  }, [envId, customizableServiceId, serviceDeploymentId]);

  const dateFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(
    navigator.language,
    {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    },
  );

  return (
    <EmptyStation title={`Deployment Logs`} subtitle={''}>
      {serviceStatusMessage ? (
        <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
      ) : (
        <Accordion
          header={
            <div className={clsx(classes.row, classes.header)}>
              <span>Action</span>
              <span>Timestamp</span>
              <span>Initiated By</span>
            </div>
          }
          stickyRows={true}
        >
          {queryResult?.serviceDeploymentLogs?.nodes.map((node) => (
            <AccordionItem
              key={node.id}
              header={
                <div className={clsx(classes.row)}>
                  <span>{node.messageType}</span>
                  <span>{dateFormatter.format(new Date(node.timestamp))}</span>
                  <span>{node.initiatedBy}</span>
                </div>
              }
            >
              <div className={clsx(classes.content)}>
                <label>Details</label>
                <pre>{JSON.stringify(node.message, undefined, 2)}</pre>
              </div>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </EmptyStation>
  );
};

export const ServiceDeploymentLogsCrumb: BreadcrumbResolver = () =>
  'Deployment Logs';
